import { nanoid } from 'nanoid';
import { create } from 'zustand';

export interface DatasheetPopoverBoundingBox {
	page: number;
	ulx: number;
	uly: number;
	lrx: number;
	lry: number;
}

/** The package evidence a datasheet was opened from. */
export interface DatasheetPopoverEvidenceSource {
	/** The package whose evidence the datasheet was opened from. */
	packageId: string;
}

/** Identifies the datasheet and the design context it was opened from. */
export interface DatasheetPopoverTarget {
	/** File store ID of the datasheet PDF. */
	fileId: string;
	/** Package-store id of the package this datasheet belongs to; enables the in-popover Replace flow. */
	pkgId?: string;
	mpn?: string;
	/** Source URL of the datasheet, when known. Used to derive a display file name. */
	datasheetUrl?: string | null;
	/** Manufacturer of the part, when known. */
	manufacturerName?: string | null;
	/** Reference designator of the module the datasheet was opened from. */
	referenceDesignator?: string;
	/** Project the module belongs to, when known. */
	projectId?: string | null;
	/** Page the PDF viewer should open to initially. */
	initialPageNumber?: number;
	/** Bounding boxes to render over the PDF when the popover opens. */
	initialBoundingBoxes?: DatasheetPopoverBoundingBox[];
	/**
	 * Set only for a datasheet opened from a package's evidence; the window then shows that package's row evidence
	 * and can follow the viewer's row pick.
	 */
	evidence?: DatasheetPopoverEvidenceSource;
}

/** A datasheet window living in its own browser window, independent of the floating popover. */
export interface ExternalDatasheetWindow {
	id: string;
	target: DatasheetPopoverTarget;
	externalWindow: Window;
}

export interface DatasheetPopoverStore {
	/** The currently open datasheet, or null when no popover is open. */
	target: DatasheetPopoverTarget | null;
	/** The document the floating popover should render into. */
	targetDocument: Document | null;
	/** Independent external-window datasheet copies, in opening order. */
	externalWindows: ExternalDatasheetWindow[];
	/** Opens the datasheet popover, replacing any datasheet already open (only one is shown at a time). */
	open: (target: DatasheetPopoverTarget, targetDocument?: Document | null) => void;
	/** Closes the datasheet popover. */
	close: () => void;
	/** Registers an already-opened browser window to host an independent datasheet copy. */
	openExternal: (target: DatasheetPopoverTarget, externalWindow: Window) => void;
	/** Removes one external datasheet window from the registry. */
	closeExternal: (id: string) => void;
}

export const useDatasheetPopoverStore = create<DatasheetPopoverStore>((set) => ({
	target: null,
	targetDocument: null,
	externalWindows: [],
	open: (target, targetDocument) => set({ target, targetDocument: targetDocument ?? document }),
	close: () => set({ target: null, targetDocument: null }),
	openExternal: (target, externalWindow) =>
		set((state) => ({
			externalWindows: [...state.externalWindows, { id: nanoid(), target, externalWindow }],
		})),
	closeExternal: (id) =>
		set((state) => ({
			externalWindows: state.externalWindows.filter((windowRecord) => windowRecord.id !== id),
		})),
}));
